<template>
  <div>
    <div class="flex gap-2">
      <div>
        <div class="main-price">
          <span v-if="label">{{ label }}</span>
          <span v-if="isGiftcard">{{ originalPrice || price }}</span>
          <span v-else>{{ price }}</span>

          <Tooltip
            v-if="!isGiftcard"
            :key="tooltipText"
            :tooltip-text="tooltipText"
            length="large"
            type="span"
          >
            <Badge v-if="type === 'PRIVATE'" class="bg-teal-50 text-primary">
              {{ $t('product.show.checkout.buyBoxPrivateBadge') }}
            </Badge>
            <InfoCircleFill v-else class="text-gray-500" />
          </Tooltip>
          <EventPromoBadge v-if="originalPrice && !isGiftcard" variant="card" />
        </div>

        <div class="leading-none flex gap-2 special-price-label">
          <template v-if="type === 'PRIVATE'">
            {{
              $t('product.show.checkout.pricePrivateSubtitle', {
                price: unitPriceFormatted,
              })
            }}
          </template>
          <template v-else-if="originalPrice && !isGiftcard">
            <span v-if="specialPriceLabel" class="">
              {{ specialPriceLabel }}
            </span>
            <span class="special-price">{{ originalPrice }}</span>

            <span
              v-if="Number(eventGetters.getPromoDaysLeft(kftEvent)) <= 7"
              class="days-left-for-promo"
            >
              {{ timeLeftText }}
            </span>
          </template>
        </div>
      </div>
      <span
        v-if="eventGetters.isBoxEvent(kftEvent) && !isGiftcard"
        class="text-gray-400 text-xs pt-2"
      >
        {{ $t('product.show.checkout.shipping.excludingShipping') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  EventDate,
  EventDateType,
  EventDescription,
} from '~/composables/types/event'
import { formatMoney } from '~/utils/currencyHelpers'
import EventPromoBadge from '~/components/product/EventPromoBadge.vue'
import InfoCircleFill from '~/public/icons/info-circle-fill.svg?component'

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: '',
  },
  specialPriceLabel: {
    type: String,
    required: false,
    default: '',
  },
  selectedTime: {
    type: Object as PropType<EventDate | undefined>,
    required: false,
  },
  kftEvent: {
    type: Object as PropType<EventDescription | undefined>,
    required: true,
  },
  type: {
    type: String as PropType<EventDateType>,
    required: true,
  },
  selectedTicketQuantity: {
    type: Number as PropType<number | null>,
    default: 1,
  },
  hasAvailableDates: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isGiftcard: {
    type: Boolean,
    default: false,
  },
})

const { locale, t } = useI18n()

const quantity = computed(() => {
  let qty = props.selectedTicketQuantity
  if (props.type === 'PRIVATE') {
    qty =
      eventDateGetters.getPrivateMinTickets(props.selectedTime) >
      (props.selectedTicketQuantity || 1)
        ? eventDateGetters.getPrivateMinTickets(props.selectedTime)
        : props.selectedTicketQuantity || 1
  }

  return qty
})

const unitPriceAmount = computed(() => {
  if (!props.kftEvent) {
    return
  }

  const p = eventDateGetters.getRawEventDatePrice(
    props.kftEvent,
    props.selectedTime
  )

  if (!p) {
    return
  }

  return Number(p)
})

const unitPriceFormatted = computed(() => {
  return formatMoney(
    (unitPriceAmount.value || 0) / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
})

const price = computed(() => {
  if (!unitPriceAmount.value) {
    return '-'
  }

  return formatMoney(
    ((unitPriceAmount.value || 0) * (quantity.value || 0)) / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
})

const originalPrice = computed(() => {
  if (!props.kftEvent) {
    return undefined
  }

  const p =
    eventDateGetters.getOriginalPriceAmount(props.selectedTime) ||
    eventGetters.getOriginalPrice(props.kftEvent)?.amount

  if (!p) {
    return undefined
  }

  return formatMoney(
    (Number(p) * quantity.value) / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
})

const tooltipText = computed(() =>
  t(
    !props.hasAvailableDates || props.isGiftcard
      ? 'product.show.checkout.info.giftcard'
      : props.type === 'PUBLIC'
        ? 'product.show.checkout.info.publicEvent'
        : 'product.show.checkout.info.privateEvent',
    {
      price: price.value,
    }
  )
)

const timeLeftText = computed(() => {
  const amountLeft = Number(eventGetters.getPromoDaysLeft(props.kftEvent))

  if (amountLeft > 0) {
    return t('product.sale.daysLeft', {
      daysLeft: amountLeft,
    })
  } else {
    return t('product.sale.hoursLeft')
  }
})
</script>

<style lang="postcss" scoped>
.main-price {
  @apply text-gray-900 text-lg font-medium text-nowrap flex gap-2 items-center;
}

.special-price {
  @apply text-xs text-gray-500 line-through gap-2;
}

.special-price-label {
  @apply text-xs text-gray-500;
}

.days-left-for-promo {
  @apply text-xs text-gray-500 text-[#DB5826] gap-2;
}
</style>
